import logoUrl from "$assets/logos/elephant-logo-colour-501px_104px.png";
import { Button } from "$components/button/button";
import { DialogBody } from "$components/dialog/dialog-body";
import { DialogContainer } from "$components/dialog/dialog-container";
import { DialogFooter } from "$components/dialog/dialog-footer";
import { DialogHeader } from "$components/dialog/dialog-header";
import RHFTextField from "$components/rhf/text-field";
import { ValidatingSubmitButton as SubmitButton } from "$components/rhf-fields";
import { useConfig } from "$global-providers/config-provider/provider";
import { toast } from "$global-providers/toast-provider";
import { useUser } from "$global-providers/user-provider";
import { usePhoneValidation } from "$routes/_lib/use-phone-validation";
import { phoneNumberValidationMessage } from "$routes/new/patient/_lib/sharedFormValidation";
import { gql } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { styled, Typography } from "@material-ui/core";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import * as Yup from "yup";
import {
  useGetUserTelephoneForUpdateModalLazyQuery,
  useUpdateUserTelephoneMutation,
} from "./__generated__/telephone-request-modal.gql";

gql`
  mutation UpdateUserTelephone($input: UpdateUserInput!) {
    updateUser(input: $input) {
      person {
        telephone
      }
    }
  }
`;

gql`
 query GetUserTelephoneForUpdateModal {
    myUser {
      _id
      person {
        telephone
      }
    }
  }
`;

const INPUT_TELEPHONE_NAME = "telephone";

export const TelephoneRequestModal: FC<{
  open?: boolean;
  onCompleted?: () => void;
  onSkip?: () => void;
  isInOnboardingFlow?: boolean;
}> = ({ open, onCompleted, onSkip, isInOnboardingFlow }) => {
  const { id: userId, firstName } = useUser();
  const [skipModalOpen, setSkipModalOpen] = useState(false);
  const { formatMessage } = useIntl();

  const phoneValidation = usePhoneValidation();
  const { territory } = useConfig();

  const [userTelephoneQuery] = useGetUserTelephoneForUpdateModalLazyQuery();

  const schemaValidation = Yup.object({
    [INPUT_TELEPHONE_NAME]: Yup.string().test(
      "phone number validation",
      phoneNumberValidationMessage(territory),
      (phoneNumber?) => phoneNumber && phoneValidation(phoneNumber).isValid,
    ),
  });

  const formMethods = useForm<Yup.InferType<typeof schemaValidation>>({
    resolver: yupResolver(schemaValidation),
    mode: "onTouched",
    defaultValues: async () => {
      const result = await userTelephoneQuery();
      const userTelephone = result.data?.myUser?.person?.telephone;
      if (!userTelephone) {
        return undefined;
      }

      const validated = phoneValidation(userTelephone);
      if (!validated.isValid) {
        return undefined;
      }

      return {
        telephone: validated.phoneNumber,
      };
    },
  });

  const [updateTelephone, { loading: updateTelephoneLoading }] =
    useUpdateUserTelephoneMutation({
      onCompleted: () => {
        toast.success(
          formatMessage({
            id: "user.updateTelephone.success",
            defaultMessage: "Telephone number has been updated!",
          }),
        );
        onCompleted?.();
      },
      onError: (error) => {
        formMethods.setError(INPUT_TELEPHONE_NAME, error);
      },
    });

  const onSubmit = ({ telephone }: { telephone: string }) => {
    updateTelephone({
      variables: {
        input: {
          _id: userId,
          person: {
            telephone: phoneValidation(telephone).phoneNumber,
          },
        },
      },
    });
  };

  return (
    <>
      <FormProvider {...formMethods}>
        <DumbTelephoneModal
          open={open}
          submitLoading={updateTelephoneLoading}
          onSubmit={formMethods.handleSubmit(onSubmit)}
          onSkip={() => setSkipModalOpen(true)}
          firstName={firstName}
          isInOnboardingFlow={isInOnboardingFlow}
        />
      </FormProvider>
      <SkipPhoneModal
        open={skipModalOpen}
        onOk={() => {
          setSkipModalOpen(false);
          onSkip();
        }}
      />
    </>
  );
};

const componentName = "TelephoneRequestModal";

export const TelephoneRequestModalTestId = {
  container: componentName,
  continueButton: `${componentName}.continueButton`,
  telephoneInput: `${componentName}.telephoneInput`,
};

const Logo = styled("img")({
  maxHeight: 52,
  alignSelf: "center",
});

const Header = styled(DialogHeader)({
  flexDirection: "column",
  alignItems: "center",
});

const Body = styled(DialogBody)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  alignItems: "flex-start",
}));

const Footer = styled(DialogFooter)({
  alignSelf: "flex-end",
});

const DumbTelephoneModal: FC<{
  open: boolean;
  submitLoading?: boolean;
  onSkip: () => void;
  onSubmit: () => void;
  isInOnboardingFlow?: boolean;
  firstName: string;
  getDefaultValues?: () => Promise<{ telephone: string } | undefined>;
}> = ({
  open,
  submitLoading,
  onSkip,
  isInOnboardingFlow,
  firstName,
  onSubmit,
}) => {
  return (
    <DialogContainer
      open={open}
      data-testid={TelephoneRequestModalTestId.container}
    >
      <Header title="">
        {isInOnboardingFlow && <Logo src={logoUrl} alt="Elephant Logo" />}
        <Typography variant="h4">
          {isInOnboardingFlow ? (
            <FormattedMessage
              id="user.telephone.request.isInOnboardingFlow.title"
              values={{ firstName }}
              defaultMessage="{firstName}, welcome to Elephant!"
            />
          ) : (
            <FormattedMessage
              id="user.telephone.request.title"
              defaultMessage="Elephant has been updated!"
            />
          )}
        </Typography>
      </Header>

      <Body>
        <form aria-label="Telephone number form" onSubmit={onSubmit}>
          <Typography variant="body2">
            {isInOnboardingFlow ? (
              <FormattedMessage
                id="user.telephone.request.isInOnboardingFlow.bodyMessage"
                defaultMessage="First, let's confirm your mobile phone number:"
              />
            ) : (
              <FormattedMessage
                id="user.telephone.request.bodyMessage"
                values={{ br: <br /> }}
                defaultMessage="As part of this update, we need to confirm a few things.{br}{br}First, please confirm your current mobile telephone number below:"
              />
            )}
          </Typography>

          <RHFTextField
            data-testid={TelephoneRequestModalTestId.telephoneInput}
            name={INPUT_TELEPHONE_NAME}
            label="Mobile phone number"
            required
            type="tel"
            // TODO: This isn't ideal - otherwise the label is in the way following addition of the async default value
            // until the user types something
            focused
          />

          <Footer>
            <Button variant="text" onClick={onSkip}>
              <FormattedMessage
                id="user.telephone.request.skip"
                defaultMessage="Skip for now"
              />
            </Button>
            <SubmitButton
              type="submit"
              data-testid={TelephoneRequestModalTestId.continueButton}
              loading={submitLoading}
            >
              {isInOnboardingFlow ? (
                <FormattedMessage
                  id="user.telephone.request.isInOnboardingFlow.confirmationBtn"
                  defaultMessage="Continue"
                />
              ) : (
                <FormattedMessage
                  id="user.telephone.request.confirmationBtn"
                  defaultMessage="Submit"
                />
              )}
            </SubmitButton>
          </Footer>
        </form>
      </Body>
    </DialogContainer>
  );
};

const SkipPhoneModal: FC<{
  open?: boolean;
  onOk?: () => void;
}> = ({ open, onOk }) => (
  <DialogContainer open={open}>
    <DialogHeader title="">
      <Typography variant="h4">
        <FormattedMessage
          id="skipPhoneModal.header"
          defaultMessage="Skipped for now"
        />
      </Typography>
    </DialogHeader>
    <Body>
      <Typography>
        <FormattedMessage
          id="skipPhoneModal.body"
          defaultMessage="You can add a mobile number at any time on the User Settings page"
        />
      </Typography>
    </Body>
    <Footer>
      <Button variant="contained" onClick={onOk}>
        <FormattedMessage id="skipPhoneModal.ok" defaultMessage="OK" />
      </Button>
    </Footer>
  </DialogContainer>
);
